/**
 * Plan contratado de la empresa (sección *Plan contratado*).
 *
 * Sólo informa y ofrece el cambio de plan: el listado y la configuración de
 * planes viven en *Configuración → Gestión de planes*.
 *
 * La tarjeta se ordena en tres bloques con el mismo patrón (rótulo + datos en
 * recuadros): **Suscripción** (dinero, fechas y vigencia), **Alcance del plan**
 * y **Módulos por categoría**. Antes eran etiquetas sueltas mezcladas con frases
 * («Hasta 0 usuarios y 0 terminales.») y una nube de etiquetas repetidas
 * (`gestion` y `GESTION` en la misma tarjeta), que costaba leer.
 */

import { CalendarClock, Crown, RefreshCw, Zap } from 'lucide-react';
import type { ReactNode } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import {
  estadoSuscripcion,
  precioSuscripcion,
  progresoSuscripcion,
  textoLimite,
  textoModulos,
} from '../lib/empresa-form';
import type { PlanContratado } from '../empresa.types';

/** Color del valor y de la barra según el estado de la suscripción. */
const TONO_VALOR = {
  ok: 'text-emerald-600',
  aviso: 'text-amber-600',
  error: 'text-rose-600',
} as const;

const TONO_BARRA = {
  ok: 'bg-emerald-500',
  aviso: 'bg-amber-500',
  error: 'bg-rose-500',
} as const;

/** Fecha corta `dd/mm/aaaa` a partir de `aaaa-mm-dd`. */
function fechaCorta(valor: string | null): string {
  if (!valor) return '—';

  const [anio, mes, dia] = valor.split('-');
  return anio && mes && dia ? `${dia}/${mes}/${anio}` : valor;
}

/** Bloque de la tarjeta: rótulo (+ dato opcional a la derecha) sobre fondo suave. */
function Bloque({
  titulo,
  extra,
  children,
}: {
  titulo: string;
  extra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-card border border-ink-100 bg-ink-50/50 p-4">
      <p className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-[11px] font-semibold tracking-wide text-ink-400 uppercase">
        <span>{titulo}</span>
        {extra ? <span className="text-[11px] font-medium tabular-nums">{extra}</span> : null}
      </p>
      {children}
    </section>
  );
}

/** Dato del resumen del plan. */
function Dato({
  etiqueta,
  valor,
  tono,
}: {
  etiqueta: string;
  valor: string;
  tono?: 'ok' | 'aviso' | 'error';
}) {
  return (
    <div className="min-w-0 rounded-control border border-ink-100 bg-white px-3 py-2">
      <p className="text-[11px] font-semibold tracking-wide text-ink-400 uppercase">{etiqueta}</p>
      <p
        className={cn('truncate text-sm font-medium', tono ? TONO_VALOR[tono] : 'text-ink-800')}
        title={valor}
      >
        {valor}
      </p>
    </div>
  );
}

interface Props {
  plan: PlanContratado | null;
  /** `false` para quien no es el administrador principal (usuario 1). */
  puedeCambiar: boolean;
  cambiando: boolean;
  error: string | null;
  onCambiar: () => void;
}

export function EmpresaPlanCard({ plan, puedeCambiar, cambiando, error, onCambiar }: Props) {
  const estado = plan ? estadoSuscripcion(plan) : null;
  const progreso = plan ? progresoSuscripcion(plan) : null;
  /** Categoría más cargada: escala de las barras (las categorías ya vienen ordenadas). */
  const mayor = plan?.categorias[0]?.total ?? 1;

  const boton = (
    <Button
      variant="outline"
      size="sm"
      leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
      loading={cambiando}
      disabled={!puedeCambiar}
      title={
        puedeCambiar ? undefined : 'Solo el administrador principal puede cambiar el plan contratado'
      }
      onClick={onCambiar}
    >
      Cambiar plan
    </Button>
  );

  if (!plan) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Plan contratado</CardTitle>
          {boton}
        </CardHeader>
        <CardContent>
          <Alert variant="warning" title="Sin plan asignado">
            La empresa no tiene un plan contratado. Mientras siga así, el panel muestra el catálogo
            completo de módulos.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2">
            <Crown className="size-4 text-amber-500" aria-hidden="true" />
            Plan contratado
          </CardTitle>
          <CardDescription>Suscripción de la empresa y módulos que incluye.</CardDescription>
        </div>
        {boton}
      </CardHeader>

      <CardContent className="flex min-h-0 flex-1 flex-col gap-4">
        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-xl font-semibold tracking-tight text-ink-900">{plan.nombre}</span>
          <Badge variant="outline">{plan.codigo}</Badge>
          <Badge variant="neutral">{plan.tipo === 'anual' ? 'Anual' : 'Mensual'}</Badge>
          <Badge variant={plan.activo === 1 ? 'success' : 'danger'}>
            {plan.activo === 1 ? 'Activa' : 'Inactiva'}
          </Badge>
        </div>

        {plan.descripcion ? <p className="text-sm text-ink-500">{plan.descripcion}</p> : null}

        <Bloque titulo="Suscripción">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Dato etiqueta="Precio contratado" valor={precioSuscripcion(plan)} />
            <Dato etiqueta="Inicio" valor={fechaCorta(plan.fecha_inicio)} />
            <Dato etiqueta="Vencimiento" valor={fechaCorta(plan.fecha_fin)} />
            <Dato etiqueta="Vigencia" valor={estado?.texto ?? '—'} tono={estado?.tono} />
          </div>

          {progreso !== null && estado ? (
            <div className="flex flex-col gap-1.5">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink-200/70">
                <div
                  className={cn('h-full rounded-full', TONO_BARRA[estado.tono])}
                  style={{ width: `${progreso}%` }}
                />
              </div>
              <p className="flex items-center gap-1.5 text-xs text-ink-500">
                <CalendarClock className="size-3.5 shrink-0" aria-hidden="true" />
                {progreso}% del período transcurrido (del {fechaCorta(plan.fecha_inicio)} al{' '}
                {fechaCorta(plan.fecha_fin)}).
              </p>
            </div>
          ) : null}
        </Bloque>

        <Bloque titulo="Alcance del plan">
          <div className="grid gap-3 sm:grid-cols-3">
            <Dato etiqueta="Módulos incluidos" valor={textoModulos(plan.total_modulos)} />
            <Dato etiqueta="Usuarios máximos" valor={textoLimite(plan.max_usuarios)} />
            <Dato etiqueta="Terminales máximos" valor={textoLimite(plan.max_terminales)} />
          </div>
        </Bloque>

        <Bloque
          titulo="Módulos por categoría"
          extra={
            plan.categorias.length > 0
              ? `${plan.categorias.length} categorías · ${textoModulos(plan.total_modulos)}`
              : undefined
          }
        >
          {plan.categorias.length > 0 ? (
            <ul className="grid gap-x-10 gap-y-3 sm:grid-cols-2">
              {plan.categorias.map((categoria) => (
                <li key={categoria.codigo} className="flex min-w-0 flex-col gap-1">
                  <span className="flex items-baseline justify-between gap-3">
                    <span className="truncate text-sm text-ink-700" title={categoria.nombre}>
                      {categoria.nombre}
                    </span>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-ink-800">
                      {categoria.total}
                    </span>
                  </span>
                  <span className="h-1 w-full overflow-hidden rounded-full bg-ink-200/70">
                    <span
                      className="block h-full rounded-full bg-brand-400"
                      style={{ width: `${Math.round((categoria.total / mayor) * 100)}%` }}
                    />
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="flex items-center gap-1.5 text-sm text-ink-500">
              <Zap className="size-3.5 shrink-0" aria-hidden="true" />
              El plan todavía no tiene módulos asignados.
            </p>
          )}
        </Bloque>
      </CardContent>
    </Card>
  );
}
